// frontend/src/components/UserManagement.js

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, IconButton, Tooltip, Toolbar, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, Checkbox, Avatar, InputAdornment
} from '@mui/material';
import { Delete, Edit, Add, Key, FilterList as FilterListIcon, Search } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import Layout from './Layout';
import { getUsers, addUser, editUser, deleteUser, resetUserPassword } from '../services/api';
import { validatePassword } from '../utils/validation';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useNavigate } from 'react-router-dom';
import { useTheme, styled } from '@mui/material/styles';


// Define header cells for the table
const headCells = [
  { id: 'Username', numeric: false, disablePadding: true, label: 'Username' },
  { id: 'FirstName', numeric: false, disablePadding: false, label: 'First Name' },
  { id: 'LastName', numeric: false, disablePadding: false, label: 'Last Name' },
  { id: 'Email', numeric: false, disablePadding: false, label: 'Email' },
  { id: 'RoleId', numeric: false, disablePadding: false, label: 'Role' },
  { id: 'actions', numeric: false, disablePadding: false, label: 'Actions' },
];

// EnhancedTableHead component to render column headers with sorting and a selection checkbox
function EnhancedTableHead({ onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort }) {
  const createSortHandler = (property) => () => { onRequestSort(property); };
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.id !== 'actions' ? (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id && (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                )}
              </TableSortLabel>
            ) : (
              headCell.label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function getInitials(user) {
  if (!user) return '';
  const f = user.FirstName || '';
  const l = user.LastName || '';
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  return (user.Username || '').substring(0, 2).toUpperCase();
}

const UserManagement = () => {
  const { showSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const theme = useTheme();

  // Load users from the API
  const loadUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response && response.users ? response.users : []);
    } catch (error) {
      if (error.status === 403) {
        showSnackbar('You do not have permission to access this resource', 'error');
        navigate('/access-denied');
      } else {
        showSnackbar('Error fetching users.', 'error');
      }
    }
  };
  useEffect(() => { loadUsers(); }, []);

  // State for add/edit modals
  const [newUser, setNewUser] = useState({
    username: '', password: '', RoleId: '2', firstName: '', lastName: '', email: '',
    countryCode: '', phoneNumber: '', jobTitle: '', department: '',
  });
  const [editUserModal, setEditUserModal] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('Username');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);


  // Apply sorting on the users list
  const filteredUsers = users.filter(
    (user) =>
      user.Username?.toLowerCase().includes(search.toLowerCase()) ||
      user.FirstName?.toLowerCase().includes(search.toLowerCase()) ||
      user.LastName?.toLowerCase().includes(search.toLowerCase()) ||
      user.Email?.toLowerCase().includes(search.toLowerCase())
  );
  const sortedUsers = filteredUsers.slice().sort((a, b) => {
    const propA = a[orderBy] || '';
    const propB = b[orderBy] || '';
    if (typeof propA === 'string') {
      return order === 'asc'
        ? propA.localeCompare(propB)
        : propB.localeCompare(propA);
    }
    return order === 'asc' ? propA - propB : propB - propA;
  });

  // Determine the rows to display in the current page
  const paginatedUsers = sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Sorting handler
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Selection handlers
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = users.map((user) => user.Id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Handle page change and rows per page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Add, edit, delete and password reset functions (as before)
  const handleAddUser = async () => {
    if (!validatePassword(newUser.password)) {
      showSnackbar(
        'Password must be at least 8 characters long and include both uppercase, lowercase letters, and numbers.',
        'error'
      );
      return;
    }
    try {
      await addUser(newUser);
      showSnackbar(`User ${newUser.username} has been created successfully.`, 'success');
      setShowAddModal(false);
      setNewUser({
        username: '',
        password: '',
        RoleId: '2',
        firstName: '',
        lastName: '',
        email: '',
        countryCode: '',
        phoneNumber: '',
        jobTitle: '',
        department: '',
      });
      loadUsers();
    } catch (error) {
      showSnackbar('Error adding user', 'error');
    }
  };

  const handleEditUser = async () => {
    if (editUserModal.password && !validatePassword(editUserModal.password)) {
      showSnackbar(
        'Password must be at least 8 characters long and include both uppercase, lowercase letters, and numbers.',
        'error'
      );
      return;
    }
    try {
      await editUser(editUserModal.Id, {
        username: editUserModal.Username,
        RoleId: editUserModal.RoleId,
        firstName: editUserModal.FirstName,
        lastName: editUserModal.LastName,
        email: editUserModal.Email,
        countryCode: editUserModal.CountryCode,
        phoneNumber: editUserModal.PhoneNumber,
        jobTitle: editUserModal.JobTitle,
        department: editUserModal.Department,
      });
      showSnackbar('User has been updated successfully.', 'success');
      setEditUserModal(null);
      loadUsers();
    } catch (error) {
      showSnackbar('Error editing user', 'error');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(selected);
      showSnackbar('Selected user(s) have been deleted successfully.', 'success');
      setSelected([]);
      setShowDeleteModal(false);
      loadUsers();
    } catch (error) {
      showSnackbar('Error deleting user', 'error');
    }
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    if (!validatePassword(password)) {
      setPasswordError(
        'Password must be at least 8 characters long and include both uppercase, lowercase letters, and numbers.'
      );
    } else {
      setPasswordError('');
    }
    setNewUser({ ...newUser, password });
  };

  const handleResetPasswordChange = (e) => {
    const password = e.target.value;
    if (!validatePassword(password)) {
      setResetPasswordError(
        'Password must be at least 8 characters long and include both uppercase, lowercase letters, and numbers.'
      );
    } else {
      setResetPasswordError('');
    }
    setResetPasswordModal({ ...resetPasswordModal, newPassword: password });
  };

  const handleResetPassword = async () => {
    if (!validatePassword(resetPasswordModal.newPassword)) {
      showSnackbar(
        'Password must be at least 8 characters long and include both uppercase, lowercase letters, and numbers.',
        'error'
      );
      return;
    }
    try {
      await resetUserPassword(resetPasswordModal.Id, resetPasswordModal.newPassword);
      showSnackbar('Password has been reset successfully.', 'success');
      setResetPasswordModal(null);
      loadUsers();
    } catch (error) {
      showSnackbar('Error resetting password', 'error');
    }
  };

  // --- UI HELPERS ---
  const renderRoleChip = (roleId) => {
    if (roleId === 1) {
      return (
        <Chip
          label="Admin"
          size="small"
          sx={{
            bgcolor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            fontWeight: 500,
          }}
        />
      );
    }
    if (roleId === 3) {
      return (
        <Chip
          label="Supervisor"
          size="small"
          sx={{
            bgcolor: theme.palette.warning.main,
            color: theme.palette.warning.contrastText,
            fontWeight: 500,
          }}
        />
      );
    }
    if (roleId === 2) {
      return (
        <Chip
          label="User"
          size="small"
          sx={{
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            fontWeight: 500,
          }}
        />
      );
    }
    return <Chip label="Unknown" size="small" />;
  };


  return (
    <Layout>
      <Box sx={{ padding: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: 'primary.main', letterSpacing: 1 }}>
          User Management
        </Typography>
        {/* Search bar and Add User */}
        <Paper elevation={0} sx={{
          p: 2, display: 'flex', alignItems: 'center', mb: 3, borderRadius: 3,
          background: (theme) => alpha(theme.palette.primary.light, 0.06),
          gap: 2,
        }}>
          <TextField
            placeholder="Search user by name, email, or username"
            value={search}
            onChange={e => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ flex: 1, background: 'white', borderRadius: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setShowAddModal(true)}
            sx={{
              borderRadius: 2,
              fontWeight: 500,
              textTransform: 'none',
              boxShadow: 'none',
              px: 3,
              py: 1,
            }}
          >
            Add User
          </Button>
        </Paper>
        {/* Table */}
        <Paper elevation={2} sx={{ borderRadius: 3, p: 2, backgroundColor: '#f9f9fb', mb: 3 }}>
          <Toolbar sx={{
            pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, borderRadius: 2, mb: 1,
            ...(selected.length > 0 && {
              bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
            }),
          }}>
            {selected.length > 0 ? (
              <Typography sx={{ flex: '1 1 100%' }} color="inherit" variant="subtitle1">
                {selected.length} selected
              </Typography>
            ) : (
              <Typography sx={{ flex: '1 1 100%' }} variant="h6" fontWeight={600} color="primary">
                Users
              </Typography>
            )}
            {selected.length > 0 ? (
              <Tooltip title="Delete">
                <IconButton onClick={() => setShowDeleteModal(true)} color="error">
                  <Delete />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Filter list">
                <span>
                  <IconButton disabled>
                    <FilterListIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Toolbar>
          <TableContainer>
            <Table stickyHeader>
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                rowCount={filteredUsers.length}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {paginatedUsers.map((user) => {
                  const isItemSelected = isSelected(user.Id);
                  return (
                    <TableRow
                      hover
                      key={user.Id}
                      selected={isItemSelected}
                      sx={{
                        cursor: 'pointer',
                        '&:last-child td': { borderBottom: 0 },
                        transition: 'background .15s',
                      }}
                      onClick={(e) => handleClick(e, user.Id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isItemSelected} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor:
                                user.RoleId === 1
                                  ? theme.palette.secondary.main
                                  : user.RoleId === 3
                                    ? theme.palette.warning.main
                                    : user.RoleId === 2
                                      ? theme.palette.primary.main
                                      : 'grey.200',
                              color:
                                user.RoleId === 1
                                  ? theme.palette.secondary.contrastText
                                  : user.RoleId === 3
                                    ? theme.palette.warning.contrastText
                                    : user.RoleId === 2
                                      ? theme.palette.primary.contrastText
                                      : 'black',
                              fontWeight: 600,
                              fontSize: 16,
                              mr: 1,
                            }}
                          >
                            {getInitials(user)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {user.Username}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{user.FirstName}</TableCell>
                      <TableCell>{user.LastName}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {user.Email}
                        </Typography>
                      </TableCell>
                      <TableCell>{renderRoleChip(user.RoleId)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Edit User">
                            <IconButton
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditUserModal(user);
                              }}
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Password">
                            <IconButton
                              color="warning"
                              onClick={(e) => {
                                e.stopPropagation();
                                setResetPasswordModal({ Id: user.Id, newPassword: '' });
                              }}
                              size="small"
                            >
                              <Key />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {paginatedUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" color="text.secondary">No users found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ '.MuiTablePagination-toolbar': { borderRadius: 2 } }}
          />
        </Paper>

        {/* Add User Modal */}
        <Dialog open={showAddModal} onClose={() => setShowAddModal(false)} fullWidth maxWidth="md">
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1.2rem', borderBottom: '1px solid #f2f2f2' }}>
            Add New User
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Username"
                  fullWidth
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Password"
                  type="password"
                  fullWidth
                  value={newUser.password}
                  onChange={handlePasswordChange}
                  error={!!passwordError}
                  helperText={passwordError}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Role"
                  fullWidth
                  value={newUser.RoleId}
                  onChange={(e) => setNewUser({ ...newUser, RoleId: e.target.value })}
                  SelectProps={{ MenuProps: { disableScrollLock: true } }}
                  required
                >
                  <MenuItem value="1">Admin</MenuItem>
                  <MenuItem value="3">Supervisor</MenuItem>
                  <MenuItem value="2">User</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  fullWidth
                  value={newUser.firstName}
                  onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  value={newUser.lastName}
                  onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Country Code"
                  fullWidth
                  value={newUser.countryCode}
                  onChange={(e) => setNewUser({ ...newUser, countryCode: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  fullWidth
                  value={newUser.phoneNumber}
                  onChange={(e) => setNewUser({ ...newUser, phoneNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Job Title"
                  fullWidth
                  value={newUser.jobTitle}
                  onChange={(e) => setNewUser({ ...newUser, jobTitle: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Department"
                  fullWidth
                  value={newUser.department}
                  onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f7f7fa', borderTop: '1px solid #f2f2f2' }}>
            <Button onClick={() => setShowAddModal(false)} color="inherit" sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button onClick={handleAddUser} variant="contained" color="primary" sx={{ textTransform: 'none' }}>
              Save User
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit User Modal */}
        {editUserModal && (
          <Dialog open={Boolean(editUserModal)} onClose={() => setEditUserModal(null)} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontWeight: 600, fontSize: '1.2rem', borderBottom: '1px solid #f2f2f2' }}>Edit User</DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Username"
                    fullWidth
                    value={editUserModal.Username || ''}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Role"
                    fullWidth
                    value={editUserModal.RoleId}
                    onChange={(e) => setEditUserModal({ ...editUserModal, RoleId: e.target.value })}
                    SelectProps={{ MenuProps: { disableScrollLock: true } }}
                    required
                  >
                    <MenuItem value="1">Admin</MenuItem>
                    <MenuItem value="3">Supervisor</MenuItem>
                    <MenuItem value="2">User</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="First Name"
                    fullWidth
                    value={editUserModal.FirstName || ''}
                    onChange={(e) => setEditUserModal({ ...editUserModal, FirstName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Last Name"
                    fullWidth
                    value={editUserModal.LastName || ''}
                    onChange={(e) => setEditUserModal({ ...editUserModal, LastName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    type="email"
                    fullWidth
                    value={editUserModal.Email || ''}
                    onChange={(e) => setEditUserModal({ ...editUserModal, Email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Country Code"
                    fullWidth
                    value={editUserModal.CountryCode || ''}
                    onChange={(e) => setEditUserModal({ ...editUserModal, CountryCode: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone Number"
                    fullWidth
                    value={editUserModal.PhoneNumber || ''}
                    onChange={(e) => setEditUserModal({ ...editUserModal, PhoneNumber: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Job Title"
                    fullWidth
                    value={editUserModal.JobTitle || ''}
                    onChange={(e) => setEditUserModal({ ...editUserModal, JobTitle: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Department"
                    fullWidth
                    value={editUserModal.Department || ''}
                    onChange={(e) => setEditUserModal({ ...editUserModal, Department: e.target.value })}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f7f7fa', borderTop: '1px solid #f2f2f2' }}>
              <Button onClick={() => setEditUserModal(null)} color="secondary">
                Cancel
              </Button>
              <Button onClick={handleEditUser} color="primary">
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Delete Confirmation Modal */}
        <Dialog open={showDeleteModal} onClose={() => setShowDeleteModal(false)} fullWidth maxWidth="xs">
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1.2rem', borderBottom: '1px solid #f2f2f2' }}>Are you sure?</DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography>
              Do you really want to delete the selected user(s)? This process cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f7f7fa', borderTop: '1px solid #f2f2f2' }}>
            <Button onClick={() => setShowDeleteModal(false)} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleDeleteUser} color="primary">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Modal */}
        {resetPasswordModal && (
          <Dialog open={Boolean(resetPasswordModal)} onClose={() => setResetPasswordModal(null)} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 600, fontSize: '1.2rem', borderBottom: '1px solid #f2f2f2' }}>Reset Password</DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <TextField
                label="New Password"
                type="password"
                fullWidth
                value={resetPasswordModal.newPassword || ''}
                onChange={handleResetPasswordChange}
                error={!!resetPasswordError}
                helperText={resetPasswordError}
                required
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f7f7fa', borderTop: '1px solid #f2f2f2' }}>
              <Button onClick={() => setResetPasswordModal(null)} color="secondary">
                Cancel
              </Button>
              <Button onClick={handleResetPassword} color="primary">
                Reset Password
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </Layout>
  );
};

export default UserManagement;
